import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'silvestr-na-chalupe',
    title: 'Silvestr na chalupě hodinu po hodině: od západu slunce po novoroční čočku',
    metaTitle: 'Silvestr na chalupě hodinu po hodině',
    perex:
      'Silvestr letos padá na čtvrtek a Nový rok na pátek, takže je před vámi čtyřdenní víkend. Připravili jsme harmonogram silvestrovského dne na chalupě od odpoledne do novoročního oběda. I s tím, proč se poslední den roku jmenuje po papeži a co řekl Havel v roce 1990.',
    coverImage: 'https://images.unsplash.com/photo-1503238774835-1e800884d53b?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Hořící prskavka v noci',
    publishedAt: '2026-12-17',
    author: 'Redakce Mátevolno',
    tags: ['Silvestr', 'Nový rok', 'zima', 'tipy'],
  },
  content: `Silvestr ve městě znamená přeplněné podniky, taxíky za trojnásobek a ohňostroje, ze kterých se pes schovává pod postel. Na chalupě je to jiný svátek. Tady je harmonogram, podle kterého můžete jet. Klidně si z něj vyberte jen polovinu.

Letos navíc hraje do karet kalendář. Silvestr připadá na čtvrtek, Nový rok na pátek a víkend hned za ním. Kdo si vezme volno od středy, má pět dní. Kdo ne, má aspoň čtyři.

## 14:00 Příjezd a poslední nákup

Přijeďte za světla. Poslední den roku je krátký a na horských silnicích se vyplatí vidět, kam jedete. A nakupte všechno hned, ideálně cestou. Na Nový rok mají podle zákona o prodejní době větší obchody zavřeno a vesnická samoobsluha na Silvestra často končí v poledne.

Seznam, na který se zapomíná: sirky, podpalovač, chleba na novoroční snídani a čočka. K té se ještě dostaneme.

## 15:30 Poslední procházka roku

Slunce na Silvestra zapadá kolem čtvrté hodiny. Poslední hodina světla je ideální na krátký výšlap na nejbližší kopec nebo rozhlednu. Podívat se na krajinu, kterou jste letos neviděli, a vrátit se za soumraku. Kdo má s sebou psa, ať ho pořádně vyvenčí teď. Večer ho čeká rachot.

## 17:00 Zatopit, prohřát, případně do sauny

Chalupa po týdnu bez lidí potřebuje čas. Zatopte hned po příjezdu, i když máte přímotopy. Silvestr je ideální večer na [saunu](/pobyty/se-saunou) nebo [horkou káď](/pobyty/s-kadi) pod širým nebem. Rok, který končí v páře, se loučí líp.

::nabidky{facility="sauna" text="Prohlédnout pobyty se saunou"}

## 19:00 Večeře bez obsluhy

Na chalupě nikdo nečeká, až se uvolní stůl. Silvestrovská večeře může trvat tři hodiny. Chlebíčky, fondue, raclette, nebo jen hrnec guláše, který se dělal od rána. Hlavní je, že nikdo nemusí nikam jet.

## 21:00 Hry a věštby

Televize počká. Karty, deskové hry, kvíz o tom, co se letos stalo. A věštění budoucnosti, které k Silvestru patří odjakživa.

Jen olovo nechte doma. Je jedovaté a v Německu se sady na lití olova od roku 2018 kvůli unijní chemické legislativě už ani neprodávají. Stejně dobře poslouží vosk ze svíček vylitý do studené vody. Proč se olovu vyhnout a další vánoční zvyky rozebíráme v článku [Šest vánočních mýtů](/inspirace/vanocni-myty-a-pravdy).

## 23:00 Pár slov o ohňostrojích

Ohňostroj nad lesem je krásný pro vás a noční můra pro všechno ostatní. Zvěř v lese, psi, kočky i ptáci. Města to začínají řešit. Praha podle [ČTK](https://www.ceskenoviny.cz/zpravy/ve-20-mestskych-castech-prahy-bude-zakazano-odpalovat-pyrotechniku/2840670) schválila v létě 2026 vyhlášku, podle které je ve dvaceti městských částech zábavní pyrotechnika zakázaná úplně. Zakázaná je také v okruhu 250 metrů od domovů pro seniory, nemocnic, útulků a zoo.

Na chalupě si zákaz dejte sami. Prskavky, lampičky na terase a hvězdy nad hlavou jsou pěkný konec roku. Na vesnici, kde je kolem tma, je vidět víc než na Letné.

## 00:00 Půlnoc a papež Silvestr

Přípitek, hymna, polibek. A kdyby se někdo zeptal, proč se ten den vlastně jmenuje Silvestr: podle [Memento Historia](https://www.memento-historia.cz/clanek/293/silvestr-i--kdo-byl-muz-podle-ktereho-se-jmenuje-posledni-den-v-roce) 31. prosince roku 335 zemřel papež Silvestr I. Podle středověké legendy vyléčil z malomocenství císaře Konstantina a pokřtil ho. Církev poslední den roku dlouho brala jako čas modlitby a rozjímání. Oslavy podobné těm dnešním se rozšířily teprve v devatenáctém století.

![Hvězdná obloha nad lesem](https://images.unsplash.com/photo-1685020437268-d49bcfe6cdf8?w=1200&q=80&auto=format&fit=crop)

## 1. 1., 10:00 Pozdní snídaně a projev

Pokud jste ještě neusnuli, novoroční den má svůj rituál. Novoroční projev prezidenta u nás podle [Radia Prague International](https://cesky.radio.cz/tradici-prezidentskych-projevu-k-vanocum-a-novemu-roku-zalozil-tgm-8577874) nepatří k nejstarším tradicím. Masaryk a Beneš mluvili k Vánocům. První novoroční projev pronesl až 1. ledna 1949 Klement Gottwald. Nejslavnější je projev Václava Havla z 1. ledna 1990, který začínal větou „Naše země nevzkvétá.“

## 1. 1., 13:00 Čočka, aby byly peníze

Na novoroční oběd patří čočka. Čím víc zrníček, tím víc peněz v novém roce. Podle tradice se na Nový rok nemá jíst drůbež, aby štěstí neuletělo, ani ryba, aby neuplavalo. A pozor na pranostiku: jak na Nový rok, tak po celý rok. Kdo celý den proleží u kamen, nemá si pak na co stěžovat.

## A pak ještě dva dny

Tohle je na letošním Silvestru nejlepší. Nový rok je v pátek, takže v sobotu a v neděli nikam nemusíte. Dva dny na chalupě bez programu jsou lepší oslava než samotná půlnoc. Kdo chce sníh, ať míří [na hory](/pobyty/pobyty-na-horach). Kdo chce klid, najde ho v [romantických pobytech pro dva](/pobyty/romanticke-pobyty). A kdo slaví s partou, vybere si velkou [chalupu](/pobyty/chalupy).

::cta{text="Najít volný termín na Silvestra"}`,
}
